/**
 * Worker pool for chunk gen/mesh jobs.
 *
 * The pool keeps no queue of its own: each worker runs one job at a time and
 * the chunk manager only hands out work while a worker is idle. That keeps
 * prioritisation (nearest chunk first, edits before streaming) in one place
 * and means a fast-moving player never waits behind a backlog of stale jobs.
 */

import { WORKER_POOL_SIZE } from '../constants';
import type { WorkerRequest, WorkerResponse, GenResponse, MeshResponse } from './chunk.worker';

export type { WorkerRequest, WorkerResponse, GenResponse, MeshResponse };

export class WorkerPool {
  private readonly workers: Worker[] = [];
  private readonly busy: boolean[] = [];
  private idleCount = 0;

  constructor(onMessage: (msg: WorkerResponse) => void) {
    for (let i = 0; i < WORKER_POOL_SIZE; i++) {
      const w = new Worker(new URL('./chunk.worker.ts', import.meta.url), { type: 'module' });
      w.onmessage = (ev: MessageEvent<WorkerResponse>) => {
        this.busy[i] = false;
        this.idleCount++;
        onMessage(ev.data);
      };
      w.onerror = (e) => console.error('chunk worker error:', e.message ?? e);
      this.workers.push(w);
      this.busy.push(false);
    }
    this.idleCount = this.workers.length;
  }

  get size(): number {
    return this.workers.length;
  }

  /** Workers with nothing to do right now. */
  get idle(): number {
    return this.idleCount;
  }

  /** Hand a job to an idle worker. Returns false (job not sent) if all are busy. */
  run(msg: WorkerRequest, transfer: Transferable[] = []): boolean {
    if (this.idleCount === 0) return false;
    const i = this.busy.indexOf(false);
    this.busy[i] = true;
    this.idleCount--;
    this.workers[i].postMessage(msg, transfer);
    return true;
  }

  /** Send a job to every worker (used for config pings). */
  broadcast(msg: WorkerRequest): void {
    for (const w of this.workers) w.postMessage(msg);
  }

  dispose(): void {
    for (const w of this.workers) w.terminate();
    this.workers.length = 0;
  }
}
